import React, { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Container, Stack, Typography, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import hackathonTheme, { displayFamily, ensureHackathonFonts, hackathonColors as c, monoFamily } from './hackathonTheme';
import logoHackathon from '../../assets/hackathon/logo-hackathon.jpg';
import headerHackathon from '../../assets/hackathon/form-header-hackathon.jpg';
import univassourasMarica from '../../assets/hackathon/univassouras-marica.png';
import facmarLogo from '../../assets/hackathon/facmar.png';

const pageBackground = [
  `radial-gradient(60rem 40rem at 85% -10%, rgba(20, 51, 110, 0.55), transparent 70%)`,
  `radial-gradient(50rem 35rem at -10% 30%, rgba(122, 21, 48, 0.3), transparent 70%)`,
  c.ink,
].join(', ');

export const gradientRule = `linear-gradient(90deg, transparent, rgba(224, 87, 122, 0.55) 15%, rgba(127, 162, 238, 0.4) 85%, transparent)`;

function CircuitLines({ style, flip = false }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="220"
      height="120"
      viewBox="0 0 220 120"
      style={{ position: 'absolute', pointerEvents: 'none', transform: flip ? 'scaleX(-1)' : undefined, ...style }}
    >
      <g fill="none" strokeWidth="1.2">
        <path d="M0 20 H70 L100 50 H180" stroke={c.rose} strokeOpacity="0.45" />
        <circle cx="185" cy="50" r="5" stroke={c.rose} strokeOpacity="0.6" />
        <path d="M0 96 H40 L60 76 H130" stroke={c.sky} strokeOpacity="0.4" />
        <circle cx="135" cy="76" r="4" stroke={c.sky} strokeOpacity="0.55" />
      </g>
    </svg>
  );
}

export function Eyebrow({ children, color = c.rose }) {
  return (
    <Typography variant="overline" component="p" sx={{ color, display: 'block' }}>
      {'</ '}{children}{' >'}
    </Typography>
  );
}

function HackathonHeader() {
  const wide = useMediaQuery(hackathonTheme.breakpoints.up('sm'));
  return (
    <Box component="header" sx={{ position: 'relative' }}>
      {wide ? (
        <img
          src={headerHackathon}
          alt="Hackathon Univassouras, Campus Maricá: 19, 20 e 21 de outubro de 2026"
          width="1024"
          height="256"
          style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 16, border: `1px solid ${c.line}` }}
        />
      ) : null}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: wide ? 2 : 0 }}>
        {!wide ? (
          <Box sx={{ flexShrink: 0, bgcolor: '#fff', borderRadius: 3, p: 0.5, lineHeight: 0, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
            <img
              src={logoHackathon}
              alt="Hackathon Universidade de Vassouras, Campus Maricá"
              width="96"
              height="64"
              style={{ display: 'block', width: 96, height: 'auto' }}
            />
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Eyebrow>Univassouras</Eyebrow>
          <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>Registro de presença</Typography>
          <Typography sx={{ fontFamily: displayFamily, fontWeight: 600, mt: 0.25, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            <Box component="span" sx={{ color: c.rose }}>Hackathon</Box>
            <Box component="span" sx={{ color: c.stone }}> · </Box>
            <Box component="span" sx={{ color: c.sky }}>Campus Maricá</Box>
          </Typography>
        </Box>
      </Stack>
      <Box aria-hidden="true" sx={{ height: '1px', mt: 2, backgroundImage: gradientRule }} />
    </Box>
  );
}

function HackathonFooter() {
  return (
    <Box component="footer" sx={{ mt: 4, pt: 2, borderTop: `1px solid ${c.line}` }}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: 1.25, py: 0.75, lineHeight: 0 }}>
          <img
            src={univassourasMarica}
            alt="Universidade de Vassouras, Campus Universitário de Maricá"
            width="157"
            height="28"
            loading="lazy"
            decoding="async"
            style={{ display: 'block', height: 28, width: 'auto' }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: c.stone, fontFamily: monoFamily }}>
          Presença gerenciada pelo <Box component="strong" sx={{ color: c.paper }}>PontoNow</Box>
        </Typography>
      </Stack>
    </Box>
  );
}

// Escopo da identidade visual: o tema global do PontoNow não é alterado.
export function HackathonShell({ children }) {
  useEffect(() => {
    ensureHackathonFonts();
  }, []);

  return (
    <ThemeProvider theme={hackathonTheme}>
      <Box
        data-testid="hackathon-shell"
        sx={{
          position: 'relative',
          minHeight: '100dvh',
          overflowX: 'hidden',
          background: pageBackground,
          color: c.paper,
          '& *:focus-visible:not(.MuiButton-contained)': { outline: `2px solid ${c.sky}`, outlineOffset: 2 },
        }}
      >
        <CircuitLines style={{ top: 8, right: -40, opacity: 0.7 }} flip />
        <CircuitLines style={{ bottom: 24, left: -60, opacity: 0.5 }} />
        <Container maxWidth="sm" sx={{ position: 'relative', py: 3, px: 2, minWidth: 0 }}>
          <HackathonHeader />
          <Box component="main" sx={{ mt: 2.5 }}>{children}</Box>
          <HackathonFooter />
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export const PRESENCE_STEPS = ['Evento', 'Localização', 'Código', 'Confirmação'];

export function StepProgress({ current }) {
  return (
    <Box component="ol" aria-label="Etapas do registro de presença" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.75 }}>
      {PRESENCE_STEPS.map((label, index) => {
        const done = index < current;
        const active = index === current;
        const tone = active ? c.rose : done ? c.sky : c.stone;
        return (
          <Box
            component="li"
            key={label}
            aria-current={active ? 'step' : undefined}
            sx={{ minWidth: 0, textAlign: 'center' }}
          >
            <Box sx={{ height: 3, borderRadius: 2, mb: 0.75, bgcolor: active || done ? tone : 'rgba(255,255,255,0.12)' }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 18, height: 18, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center',
                  fontFamily: monoFamily, fontSize: 11, lineHeight: 1,
                  border: `1px solid ${tone}`, color: active ? c.ink : tone, bgcolor: active ? c.rose : 'transparent',
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 12 }} /> : index + 1}
              </Box>
              <Typography
                component="span"
                sx={{ fontFamily: displayFamily, fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: active ? c.paper : tone, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {label}
                <Box component="span" sx={visuallyHidden}>{done ? ' (concluída)' : active ? ' (atual)' : ''}</Box>
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

const visuallyHidden = {
  position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

export function SectionTitle({ icon: Icon, eyebrow, children }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      {Icon ? (
        <Box
          aria-hidden="true"
          sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: c.paper, backgroundImage: `linear-gradient(135deg, ${c.wine}, ${c.navy})` }}
        >
          <Icon fontSize="small" />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        {eyebrow ? <Eyebrow color={c.sky}>{eyebrow}</Eyebrow> : null}
        <Typography variant="h3" component="h2">{children}</Typography>
      </Box>
    </Stack>
  );
}

export const isFacmarContext = (...names) => names.some((name) => /hack\s*enf|facmar|enfermagem/i.test(String(name || '')));

export function FacmarBadge() {
  return (
    <Box sx={{ display: 'inline-flex', bgcolor: '#fff', borderRadius: 2, px: 1, py: 0.5, lineHeight: 0 }}>
      <img
        src={facmarLogo}
        alt="FACMAR, Faculdade de Ciências Médicas de Maricá"
        width="79"
        height="32"
        loading="lazy"
        decoding="async"
        style={{ display: 'block', height: 32, width: 'auto' }}
      />
    </Box>
  );
}
